// src/components/vocabulary/VocabularyCard.jsx
// Enhanced vocabulary card with article colors and word highlighting
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { RotateCw, Volume2, Star, Tag } from 'lucide-react';
import SwipeIndicators, { DragDirectionIndicator } from './SwipeIndicators';
import { CardState } from '../../services/srsService';
import ttsService from '../../services/ttsService';
import { useTranslation } from 'react-i18next';

/**
 * Get article-based styling
 */
const getArticleStyle = (article) => {
  switch(article?.toLowerCase()) {
    case 'der':
      return {
        gradient: 'from-blue-400 to-blue-600',
        indicator: 'bg-blue-500',
        textColor: 'text-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-300',
        highlightColor: '#2563eb' // blue-600
      };
    case 'die':
      return {
        gradient: 'from-pink-400 to-pink-600',
        indicator: 'bg-pink-500',
        textColor: 'text-pink-600',
        bgColor: 'bg-pink-50',
        borderColor: 'border-pink-300',
        highlightColor: '#ec4899' // pink-600
      };
    case 'das':
      return {
        gradient: 'from-green-400 to-green-600',
        indicator: 'bg-green-500',
        textColor: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-300',
        highlightColor: '#16a34a' // green-600
      };
    default:
      return {
        gradient: 'from-gray-400 to-gray-600',
        indicator: 'bg-gray-500',
        textColor: 'text-gray-600',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-300',
        highlightColor: '#4b5563' // gray-600
      };
  }
};

/**
 * Highlight the target word in example sentences
 */
const highlightWord = (text, word, color) => {
  console.log('Highlighting:', { text, word, color });

  if (!text || !word) {
    console.log('No text or word provided');
    return text;
  }

  // Remove articles from the word
  const articles = ['der', 'die', 'das', 'den', 'dem', 'des', 'ein', 'eine'];
  let cleanWord = word.trim();

  // Check if starts with article and remove it
  const wordParts = cleanWord.split(' ');
  if (wordParts.length > 1 && articles.includes(wordParts[0].toLowerCase())) {
    cleanWord = wordParts.slice(1).join(' ');
  }

  // IMPORTANT: Remove grammatical notations like ", -n" or ", -en" etc.
  // This handles formats like "Nachname, -n" or "Tisch, -e"
  cleanWord = cleanWord.split(',')[0].trim();

  console.log('Clean word to highlight:', cleanWord);

  // Escape special regex characters
  const escapedWord = cleanWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  try {
    // Create regex that matches the word and its variations
    // For "Nachname", this will match "Nachname", "Nachnamen", "Nachnamens"
    const regex = new RegExp(`\\b(${escapedWord}[a-zäöüßA-ZÄÖÜ]*)\\b`, 'g');

    let result = [];
    let lastIndex = 0;
    let match;

    const textString = String(text);

    while ((match = regex.exec(textString)) !== null) {
      console.log('Found match:', match[0], 'at index:', match.index);

      // Add text before match
      if (match.index > lastIndex) {
        result.push(textString.slice(lastIndex, match.index));
      }

      // Add highlighted match
      result.push(
        <strong
          key={match.index}
          style={{
            color: color || '#2563eb',
            backgroundColor: `${color}20` || '#2563eb20',
            padding: '1px 3px',
            borderRadius: '3px',
            fontWeight: 'bold'
          }}
        >
          {match[0]}
        </strong>
      );

      lastIndex = regex.lastIndex;
    }

    // Add remaining text
    if (lastIndex < textString.length) {
      result.push(textString.slice(lastIndex));
    }

    console.log('Result parts:', result.length);

    return result.length > 1 ? result : text; // Changed from > 0 to > 1

  } catch (error) {
    console.error('Error in highlightWord:', error);
    return text;
  }
};

/**
 * Enhanced Vocabulary Card Component
 */
const VocabularyCard = ({
  card,
  onSwipe,
  isVisible = true,
  zIndex = 0,
  showIndicators = true,
  enableAudio = true,
  onFlipChange = null
}) => {
  const { t } = useTranslation();
  const [isFlipped, setIsFlipped] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [revealedExamples, setRevealedExamples] = useState({});
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);

  // Reset flip state when card changes
  useEffect(() => {
    setIsFlipped(false);
    if (onFlipChange) {
      onFlipChange(false);
    }
  }, [card.id]);

  const cardRef = useRef(null);
  const startPos = useRef({ x: 0, y: 0 });
  const audioRef = useRef(null);
  // Get article styling
  const articleStyle = getArticleStyle(card.article);
  const [isMobile, setIsMobile] = useState(false);
  
  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Keyboard shortcuts for pronunciation
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Skip if card is not visible or user is dragging
      if (!isVisible || isDragging) return;
      
      // Space: Play sound when card is flipped (better UX)
      if (e.code === 'Space' && isFlipped && !isPlayingAudio) {
        e.preventDefault();
        playPronunciation(e);
      }
      
      // Ctrl/Cmd + V: Play sound anytime
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && !isPlayingAudio) {
        e.preventDefault();
        playPronunciation(e);
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isFlipped, isPlayingAudio, isDragging, isVisible]);
  

  // Card state badge
  const getStateBadge = () => {
    switch(card.state) {
      case CardState.NEW:
        return { text: 'Neu', color: 'bg-blue-500' };
      case CardState.LEARNING:
        return { text: 'Lernen', color: 'bg-yellow-500' };
      case CardState.REVIEW:
        return { text: 'Wiederholen', color: 'bg-green-500' };
      case CardState.RELEARNING:
        return { text: 'Neu lernen', color: 'bg-orange-500' };
      default:
        return null;
    }
  };

  const stateBadge = getStateBadge();

  // Handle drag start
  const handleStart = useCallback((clientX, clientY) => {
    if (isFlipped) return; // Don't allow swipe when flipped
    setIsDragging(true);
    startPos.current = { x: clientX, y: clientY };
    document.body.style.userSelect = 'none';
  }, [isFlipped]);

  // Handle drag move
  const handleMove = useCallback((clientX, clientY) => {
  if (!isDragging || isFlipped) return;
  const deltaX = clientX - startPos.current.x;
  // Only track X movement, ignore Y
  setDragOffset({ x: deltaX, y: 0 });
}, [isDragging, isFlipped]);

  // Handle drag end
  const handleEnd = useCallback(() => {
    if (!isDragging) return;

    const threshold = 100;
    if (Math.abs(dragOffset.x) > threshold) {
      const direction = dragOffset.x > 0 ? 'know' : 'dontknow';
      onSwipe(card.id, direction);
    }

    setIsDragging(false);
    setDragOffset({ x: 0, y: 0 });
    document.body.style.userSelect = '';
  }, [isDragging, dragOffset.x, card.id, onSwipe]);

  // Touch handlers
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    handleStart(touch.clientX, touch.clientY);
  };

  const handleTouchMove = (e) => {
    const touch = e.touches[0];
    handleMove(touch.clientX, touch.clientY);
  };

  // Mouse handlers
  const handleMouseDown = (e) => {
    handleStart(e.clientX, e.clientY);
  };

  const handleMouseMove = (e) => {
    handleMove(e.clientX, e.clientY);
  };

  // Flip card handler
  const handleFlip = () => {
    if (!isDragging) {
      setIsFlipped(!isFlipped);
      // Notify parent component of flip state change
      if (onFlipChange) {
        onFlipChange(!isFlipped);
      }
      // Play flip sound if available
      if (audioRef.current) {
        audioRef.current.play().catch(() => {});
      }
    }
  };

  // Toggle example visibility
  const toggleExample = (e, index) => {
    e.stopPropagation();
    setRevealedExamples(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Play pronunciation (uses high-quality TTS with fallback, clear voice for learning)
  const playPronunciation = async (e) => {
    e.stopPropagation();
    if (!card?.front || isPlayingAudio) return;
    
    setIsPlayingAudio(true);
    try {
      await ttsService.speak(card.front, { 
        voice: 'female_clear',  // Clear voice for effective vocabulary learning
        speed: 0.85,            // Slower for comprehension
        cache: true,            // Cache to prevent redundant API calls
        priority: 'high'        // High priority for vocabulary
      });
    } catch (err) {
      console.warn('Pronunciation unavailable:', err?.message || err);
    } finally {
      setIsPlayingAudio(false);
    }
  };

  return (
    <div
      className={`absolute top-0 left-0 w-full h-full transition-all duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      style={{
        zIndex,
        // Only apply transforms when dragging to prevent GPU layer when idle
        transform: isDragging 
          ? `translate3d(${dragOffset.x}px, ${Math.abs(dragOffset.x) * 0.02}px, 0) rotate(${dragOffset.x * 0.15}deg)`
          : 'none',
        transformOrigin: 'center 90%', // Rotation pivot near bottom
        opacity: 1 - Math.abs(dragOffset.x) / 500, // Fade out as it moves
        transition: isDragging ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        // Only use will-change when actively dragging to prevent permanent GPU layer
        willChange: isDragging ? 'transform, opacity' : 'auto',
        // Ensure card doesn't exceed parent bounds
        maxWidth: '100%',
        maxHeight: '100%'
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleEnd}
      onMouseLeave={handleEnd}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleEnd}
    >
      {/* Swipe Indicators */}
      {showIndicators && (
        <>
          <SwipeIndicators
            isDragging={isDragging}
            dragOffset={dragOffset}
            isFlipped={isFlipped}
            showHints={!isFlipped}
          />
          <DragDirectionIndicator
            dragOffset={dragOffset}
            isDragging={isDragging}
          />
        </>
      )}

      {/* Card Container. Conditional 3D/2D flip based on device */}
      <div
        ref={cardRef}
        onClick={handleFlip}
        className="w-full h-full cursor-pointer touch-manipulation relative"
        style={isMobile ? {} : { perspective: '1000px' }}
      >
        {isMobile ? (
          /* Mobile: Simple opacity-based flip - NO 3D transforms to prevent blur */
          <>
            {/* Front of Card - Mobile */}
            <div
              className="w-full h-full absolute inset-0"
              style={{
                opacity: isFlipped ? 0 : 1,
                pointerEvents: isFlipped ? 'none' : 'auto',
                transition: 'opacity 0.5s ease-in-out',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
                textRendering: 'optimizeLegibility',
                zIndex: isFlipped ? 1 : 2
              }}
            >
              <div className="w-full h-full bg-white rounded-xl sm:rounded-2xl shadow-xl border-2 border-gray-200 flex flex-col overflow-hidden">
              {/* Article Color Indicator */}
              <div className={`w-full h-1.5 sm:h-2 ${articleStyle.indicator} flex-shrink-0`} />

              {/* State Badge */}
              {stateBadge && (
                <div className="absolute top-2 right-2 z-10">
                  <span className={`${stateBadge.color} text-white text-[10px] px-1.5 py-0.5 rounded-full font-medium`}>
                    {stateBadge.text}
                  </span>
                </div>
              )}

              {/* Centered content container */}
              <div className="flex flex-col items-center justify-center flex-1 p-2.5 sm:p-8 gap-4 sm:gap-6">
                {/* Icon Section */}
                <div className={`w-full max-w-[120px] sm:max-w-[200px] aspect-square rounded-lg sm:rounded-xl flex items-center justify-center bg-gradient-to-br ${articleStyle.gradient} flex-shrink-0`}>
                  <div className="text-xl sm:text-4xl md:text-5xl">{card.icon || '📚'}</div>
                </div>

                {/* Word Section */}
                <div className="flex flex-col items-center text-center gap-2 sm:gap-4">
                  {card.article && (
                    <p className={`text-xs sm:text-sm font-medium ${articleStyle.textColor}`} style={{
                      WebkitFontSmoothing: 'antialiased',
                      MozOsxFontSmoothing: 'grayscale',
                      textRendering: 'optimizeLegibility'
                    }}>
                      {card.article}
                    </p>
                  )}
                  <h2 className="text-xl sm:text-[clamp(1.5rem,4vw,2.5rem)] font-bold text-gray-900" style={{
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                    textRendering: 'optimizeLegibility'
                  }}>
                    {card.front}
                  </h2>

                  {/* Audio Button */}
                  {enableAudio && (
                    <button
                      onClick={playPronunciation}
                      disabled={isPlayingAudio}
                      className={`min-w-[44px] min-h-[44px] p-1.5 rounded-full transition-all flex items-center justify-center touch-manipulation group relative ${
                        isPlayingAudio 
                          ? 'bg-blue-100 animate-pulse' 
                          : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                      aria-label="Play pronunciation"
                      title="Play pronunciation (female, clear voice)"
                    >
                      <Volume2 className={`w-4 h-4 sm:w-5 sm:h-5 transition-colors ${
                        isPlayingAudio ? 'text-blue-600' : 'text-gray-600'
                      }`} />
                      <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                        {isPlayingAudio ? 'Playing...' : 'Pronunciation'}
                      </span>
                    </button>
                  )}

                  <div className="flex items-center justify-center gap-1.5 text-[10px] sm:text-sm text-gray-400">
                    <RotateCw className="w-3 h-3 sm:w-4 sm:h-4 animate-spin [animation-duration:3s]" />
                    <span>{t('vocabulary.card.tapToFlip')}</span>
                  </div>
                </div>
              </div>
              </div>
            </div>

            {/* Back of Card - Mobile */}
            <div
              className="w-full h-full absolute inset-0"
              style={{
                opacity: isFlipped ? 1 : 0,
                pointerEvents: isFlipped ? 'auto' : 'none',
                transition: 'opacity 0.5s ease-in-out',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
                textRendering: 'optimizeLegibility',
                zIndex: isFlipped ? 2 : 1
              }}
            >
              <div className="w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl shadow-xl border-2 border-gray-200 p-2.5 sm:p-6 flex flex-col overflow-hidden">
            {/* Translation */}
            <div className="text-center pb-1.5 sm:pb-3 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-lg sm:text-2xl font-bold text-gray-800" style={{
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
                textRendering: 'optimizeLegibility'
              }}>{card.back}</h3>
            </div>

            {/* Examples Header, moved to top with icon */}
            {card.examples && card.examples.length > 0 && (
              <div className={`flex items-center justify-center gap-2 py-2 px-3 ${articleStyle.bgColor} ${articleStyle.borderColor} border-x-0 border-t-0 border-b-2 flex-shrink-0`}>
                <Tag className={`w-4 h-4 ${articleStyle.textColor}`} />
                <h4 className="text-sm font-semibold text-gray-700">{t('vocabulary.card.examples')}:</h4>
              </div>
            )}

            {/* Examples Section */}
            <div className="space-y-1.5 sm:space-y-3 overflow-y-auto pt-1.5 sm:pt-3 flex-1 min-h-0">
              {card.examples && card.examples.length > 0 ? (
                <>
                  {card.examples.map((example, i) => (
                    <div
                      key={i}
                      className={`p-2 sm:p-3 rounded-lg ${articleStyle.bgColor} ${articleStyle.borderColor} border cursor-pointer transition-all hover:shadow-md min-h-[44px] touch-manipulation`}
                      onClick={(e) => toggleExample(e, i)}
                    >
                      <p className="text-sm sm:text-base text-gray-800 leading-relaxed" style={{
                        WebkitFontSmoothing: 'antialiased',
                        MozOsxFontSmoothing: 'grayscale',
                        textRendering: 'optimizeLegibility'
                      }}>
                        {highlightWord(example.german || example.de || example.text || '', card.front, articleStyle.highlightColor)}
                      </p>
                      {!revealedExamples[i] && (
                        <p className="text-center text-[10px] sm:text-xs font-semibold text-gray-400 mt-0.5">
                          {t('vocabulary.card.tapForTranslation')}
                        </p>
                      )}

                      {revealedExamples[i] && example.english && (
                        <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2 italic" style={{
                          WebkitFontSmoothing: 'antialiased',
                          MozOsxFontSmoothing: 'grayscale',
                          textRendering: 'optimizeLegibility'
                        }}>
                          {example.english}
                        </p>
                      )}
                    </div>
                  ))}
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  <p>{t('vocabulary.card.noExamples')}</p>
                </div>
              )}
            </div>

            {/* Tags */}
            {card.tags && card.tags.length > 0 && (
              <div className="pt-1.5 sm:pt-4 border-t border-gray-200 flex flex-wrap gap-1.5 sm:gap-2 flex-shrink-0">
                {card.tags.map((tag, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 px-2 py-0.5 sm:py-1 bg-gray-200 text-gray-700 rounded-full text-[10px] sm:text-xs"
                  >
                    <Tag className="w-3 h-3" />
                    {tag}
                  </span>
                ))}
              </div>
            )}
              </div>
            </div>
          </>
        ) : (
          /* Desktop: 3D flip animation */
          <div
            className="w-full h-full relative"
            style={{
              transformStyle: 'preserve-3d',
              transition: 'transform 0.5s ease-in-out',
              transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
            }}
          >
            {/* Front of Card - Desktop */}
            <div
              className="absolute inset-0 w-full h-full"
              style={{
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                transform: 'rotateY(0deg)',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
                textRendering: 'optimizeLegibility'
              }}
            >
              <div className="w-full h-full bg-white rounded-xl sm:rounded-2xl shadow-xl border-2 border-gray-200 flex flex-col overflow-hidden">
                {/* Article Color Indicator */}
                <div className={`w-full h-1.5 sm:h-2 ${articleStyle.indicator} flex-shrink-0`} />

                {/* State Badge */}
                {stateBadge && (
                  <div className="absolute top-2 right-2 z-10">
                    <span className={`${stateBadge.color} text-white text-[10px] px-1.5 py-0.5 rounded-full font-medium`}>
                      {stateBadge.text}
                    </span>
                  </div>
                )}

                {/* Centered content container */}
                <div className="flex flex-col items-center justify-center flex-1 p-2.5 sm:p-8 gap-4 sm:gap-6">
                  {/* Icon Section */}
                  <div className={`w-full max-w-[120px] sm:max-w-[200px] aspect-square rounded-lg sm:rounded-xl flex items-center justify-center bg-gradient-to-br ${articleStyle.gradient} flex-shrink-0`}>
                    <div className="text-xl sm:text-4xl md:text-5xl">{card.icon || '📚'}</div>
                  </div>

                  {/* Word Section */}
                  <div className="flex flex-col items-center text-center gap-2 sm:gap-4">
                    {card.article && (
                      <p className={`text-xs sm:text-sm font-medium ${articleStyle.textColor}`} style={{
                        WebkitFontSmoothing: 'antialiased',
                        MozOsxFontSmoothing: 'grayscale',
                        textRendering: 'optimizeLegibility'
                      }}>
                        {card.article}
                      </p>
                    )}
                    <h2 className="text-xl sm:text-[clamp(1.5rem,4vw,2.5rem)] font-bold text-gray-900" style={{
                      WebkitFontSmoothing: 'antialiased',
                      MozOsxFontSmoothing: 'grayscale',
                      textRendering: 'optimizeLegibility'
                    }}>
                      {card.front}
                    </h2>

                    {/* Audio Button */}
                    {enableAudio && (
                      <button
                        onClick={playPronunciation}
                        className="min-w-[44px] min-h-[44px] p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center touch-manipulation"
                        aria-label="Play pronunciation"
                      >
                        <Volume2 className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                      </button>
                    )}

                    <div className="flex items-center justify-center gap-1.5 text-[10px] sm:text-sm text-gray-400">
                      <RotateCw className="w-3 h-3 sm:w-4 sm:h-4 animate-spin [animation-duration:3s]" />
                      <span>{t('vocabulary.card.tapToFlip')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Back of Card - Desktop */}
            <div
              className="absolute inset-0 w-full h-full"
              style={{
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                transform: 'rotateY(-180deg)', // Use -180deg to avoid mirroring, then container's +180deg brings it to 0deg
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
                textRendering: 'optimizeLegibility'
              }}
            >
              <div className="w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl shadow-xl border-2 border-gray-200 p-2.5 sm:p-6 flex flex-col overflow-hidden">
                {/* Translation */}
                <div className="text-center pb-1.5 sm:pb-3 border-b border-gray-200 flex-shrink-0">
                  <h3 className="text-lg sm:text-2xl font-bold text-gray-800" style={{
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                    textRendering: 'optimizeLegibility'
                  }}>{card.back}</h3>
                </div>

                {/* Examples Header */}
                {card.examples && card.examples.length > 0 && (
                  <div className={`flex items-center justify-center gap-2 py-2 px-3 ${articleStyle.bgColor} ${articleStyle.borderColor} border-x-0 border-t-0 border-b-2 flex-shrink-0`}>
                    <Tag className={`w-4 h-4 ${articleStyle.textColor}`} />
                    <h4 className="text-sm font-semibold text-gray-700">{t('vocabulary.card.examples')}:</h4>
                  </div>
                )}

                {/* Examples Section */}
                <div className="space-y-1.5 sm:space-y-3 overflow-y-auto pt-1.5 sm:pt-3 flex-1 min-h-0">
                  {card.examples && card.examples.length > 0 ? (
                    <>
                      {card.examples.map((example, i) => (
                        <div
                          key={i}
                          className={`p-2 sm:p-3 rounded-lg ${articleStyle.bgColor} ${articleStyle.borderColor} border cursor-pointer transition-all hover:shadow-md min-h-[44px] touch-manipulation`}
                          onClick={(e) => toggleExample(e, i)}
                        >
                          <p className="text-sm sm:text-base text-gray-800 leading-relaxed" style={{
                            WebkitFontSmoothing: 'antialiased',
                            MozOsxFontSmoothing: 'grayscale',
                            textRendering: 'optimizeLegibility'
                          }}>
                            {highlightWord(example.german || example.de || example.text || '', card.front, articleStyle.highlightColor)}
                          </p>
                          {!revealedExamples[i] && (
                            <p className="text-center text-[10px] sm:text-xs font-semibold text-gray-400 mt-0.5">
                              {t('vocabulary.card.tapForTranslation')}
                            </p>
                          )}

                          {revealedExamples[i] && example.english && (
                            <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2 italic" style={{
                              WebkitFontSmoothing: 'antialiased',
                              MozOsxFontSmoothing: 'grayscale',
                              textRendering: 'optimizeLegibility'
                            }}>
                              {example.english}
                            </p>
                          )}
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className="flex-1 flex items-center justify-center text-gray-500">
                      <p>{t('vocabulary.card.noExamples')}</p>
                    </div>
                  )}
                </div>

                {/* Tags */}
                {card.tags && card.tags.length > 0 && (
                  <div className="pt-1.5 sm:pt-4 border-t border-gray-200 flex flex-wrap gap-1.5 sm:gap-2 flex-shrink-0">
                    {card.tags.map((tag, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 px-2 py-0.5 sm:py-1 bg-gray-200 text-gray-700 rounded-full text-[10px] sm:text-xs"
                      >
                        <Tag className="w-3 h-3" />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                </div>
            </div>
          </div>
        )}
      </div>

      {/* Hidden audio element for flip sound */}
      <audio ref={audioRef} src="/sfx/flip.mp3" preload="auto" />
    </div>
  );
};

export default VocabularyCard;